import React, { useCallback, useState } from 'react';
import { createSearchParams, useLocation, generatePath } from 'react-router-dom';
import { useNavigate } from 'src/utils/useNavigate';
import routes from 'src/constants/routes';
import { CheckBox, Link } from '@1c/ui';
import { toast } from 'react-toastify';
import Upload from 'rc-upload';
import LinkButton from 'src/components/LinkButton';
import RouterLink from 'src/components/Link';
import useWithAuthorities from 'src/utils/useWithAuthorities';
import permissions from 'src/constants/permissions';
import { getDataToSignByExternalSoftwareAPI } from 'src/serverApi/joiningWithSignature';
import Text from 'src/components/Text';
import styles from './styles.module.scss';

const ApproveByThirdPartySoftware: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const withDataManagementAuthorities = useWithAuthorities([permissions.dataManagement]);
  const [offerAgreementChecked, setOfferAgreementChecked] = useState(false);

  const handleOfferAgreementClick = (value: any) => {
    setOfferAgreementChecked(value?.currentTarget?.checked);
  };

  const onApproveSuccess = useCallback((data: any) => {
    toast.success(
      'Проверка сертификата и ЭП выполнена успешно! Право работать с данными организации подтверждено!'
    );
    const url = data?.organizationUeid
      ? generatePath(routes.organization, { organizationUeid: data?.organizationUeid })
      : routes.agent;
    navigate(url);
  }, []);

  const onApproveFail = useCallback(
    (err: any, responseBody: any) => {
      const searchParams = createSearchParams({
        returnUrl: `${location.pathname}${location.search}`,
      }).toString();

      if (err?.status === 403 || responseBody?.type === 'ACTION_IS_NOT_ALLOWED') {
        return navigate({
          pathname: routes.phoneConfirm,
          search: searchParams,
        });
      }
      if (
        err?.status === 401 ||
        (err?.status === 400 && responseBody?.type === 'USER_ID_NOT_SPECIFIED')
      ) {
        return navigate({
          pathname: routes.authorization,
          search: searchParams,
        });
      }

      if (err?.status === 422 && responseBody.message) {
        toast.error(<div style={{ whiteSpace: 'pre-wrap' }}>{responseBody.message}</div>);
      } else {
        toast.error('При проверке сертификата и ЭП произошла ошибка.');
      }
    },
    [location.pathname, location.search, navigate]
  );

  // При попытке скачать файл в зависимости от состояния авторизации БЭ может прислать
  // в ответ файл или же json с ошибкой. Если использовать ссылку с аттрибутом download,
  // то обработать вариант с ошибкой невозможно. Браузер не предоставляет интерфейса для
  // перехвата ошибки встроенного в него загрузчика. В результате ошибки авторизации
  // не обрабатываются и не редиректят, сообщения об ошибке не показываются, а загрузчик
  // показывает файлы с расширением ".json" и предупреждением о блокировке доступа к ним.
  // Единственный вариант сохранить контроль и обработать эти сценарии
  // - это делать всё через JS.
  const handleDataToSignClick = async (evt: any) => {
    evt.preventDefault();
    try {
      await withDataManagementAuthorities(async () => {
        const dataToSignByExternalSoftware = await getDataToSignByExternalSoftwareAPI();
        const blob = new Blob([dataToSignByExternalSoftware], {
          type: 'text/plain',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'to-sign');
        document.body.append(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      toast.error('Не удалось скачать файл');
    }
  };

  return (
    <div>
      <Link to="#" onClick={handleDataToSignClick} icon="download" external>
        Скачайте файл
      </Link>
      <Text type="span">
        . Передайте его сотруднику, у которого есть действующий сертификат и ЭП для указанной
        организации.
      </Text>
      <br />
      <Text type="span">Попросите его подписать данный файл с помощью ЭП.</Text>
      <br />
      <Upload
        disabled={!offerAgreementChecked}
        action={
          '/ui/front-api/private/organizations/joining-with-signature/join-by-signature-with-external-software'
        }
        onSuccess={onApproveSuccess}
        onError={onApproveFail}
        accept=".sig, .p7s"
      >
        <LinkButton onClick={() => {}} disabled={!offerAgreementChecked} className={styles['link']}>
          Подписанный файл (в формате .sig) загрузите на портал для проверки.
        </LinkButton>
      </Upload>
      <div className={styles['checkbox-wrapper']}>
        <CheckBox
          className={styles['checkbox']}
          checked={offerAgreementChecked}
          onChange={handleOfferAgreementClick}
        >
          Загружая подписанный файл, я принимаю условия{' '}
        </CheckBox>
        <RouterLink to={routes.offer} target="_blank">
          договора оферты
        </RouterLink>
      </div>
    </div>
  );
};

export default ApproveByThirdPartySoftware;
